import { z } from 'zod';

const num = () => z.coerce.number();
const numNull = () => z.union([z.coerce.number(), z.null()]);

/* Monthly Merch KPI cards (8170–8172). All three share the
   (month, brand_name, brand_manager, merch_portfolio) grain, months relative
   to today (last 9 months; size availability carries 6 month-end snapshots +
   live). Brand grain (added 2026-06-11) powers the per-BM brand breakdown. */

export const MonthlyKpiRevenueSchema = z.array(
  z.object({
    month: z.string(),
    brand_name: z.string(),
    brand_manager: z.string(),
    merch_portfolio: z.string(),
    net_gmv: num(),
    online_net_gmv: num(),
  }),
);
export type MonthlyKpiRevenue = z.infer<typeof MonthlyKpiRevenueSchema>;

export const MonthlyKpiSizeAvailabilitySchema = z.array(
  z.object({
    month: z.string(),
    brand_name: z.string(),
    brand_manager: z.string(),
    merch_portfolio: z.string(),
    /* Merged-Coyu snapshot lens (2026-08-21): styles are counted once across
     included STORE + WAREHOUSE facilities, with unique sizes unioned across
     locations. Current month uses live stock; prior months use month-end snapshots. */
    in_stock_styles: num(),
    total_unique_sizes: num(),
    avg_size_availability: num(),
    styles_3plus_sizes: num().optional().default(0),
    days_counted: num().optional(),
  }),
);
export type MonthlyKpiSizeAvailability = z.infer<typeof MonthlyKpiSizeAvailabilitySchema>;

/* Brand Health Score components — same seven-drag formula as the
   MTO / MTC / JIT-B2B Health report (card 7763 / Dashboard 195). */
export const MonthlyKpiCxSchema = z.array(
  z.object({
    month: z.string(),
    brand_name: z.string(),
    brand_manager: z.string(),
    merch_portfolio: z.string(),
    shared_lines: num(),
    total_customers: num(),
    declined_lines: num(),
    wh_delay_1d_scope_lines: num(),
    wh_delay_1d_lines: num(),
    wh_delay_7d_scope_lines: num(),
    wh_delay_7d_lines: num(),
    delay_cancelled_lines: num(),
    wh_delayed_without_timely_revision_lines: num(),
    decline_days_sum: numNull(),
    decline_days_cnt: num(),
    impacted_customers: num(),
    /* Customer-distinct counts deduped across the BM's whole book — exact for
     BM rollups (brand-grain distincts double-count cross-brand customers). */
    bm_total_customers: numNull().optional(),
    bm_impacted_customers: numNull().optional(),
    /* Customer-distinct counts deduped across the portfolio's whole book —
     exact for portfolio rollups when the BM filter is not narrowing scope. */
    portfolio_total_customers: numNull().optional(),
    portfolio_impacted_customers: numNull().optional(),
  }),
);
export type MonthlyKpiCx = z.infer<typeof MonthlyKpiCxSchema>;

/* Per-BM monthly targets (card 9697) over
   mesh.stg_supabase_monthly_brand_manager_targets — one row per
   (month, brand_manager), latest non-deleted revision. `month` is 'YYYY-MM-01'.
   Every target field is nullable: a null means "no mart target for this KPI",
   and the report falls back to targets.ts. */
export const MonthlyKpiBrandManagerTargetSchema = z.array(
  z.object({
    month: z.string(),
    brand_manager: z.string(),
    revenue_gmv: numNull(),
    size_availability: numNull(),
    styles_with_3_plus_sizes: numNull(),
    online_sales_growth: numNull(),
    customer_mto_jit_experience_score: numNull(),
  }),
);
export type MonthlyKpiBrandManagerTarget = z.infer<typeof MonthlyKpiBrandManagerTargetSchema>;
